import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { type MouseEventHandler, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { Footer } from '@verantos-dev/shared-components';

import type { SidebarConfig } from './types';
import { useSidebar } from './useSidebar';
import { AppHeader } from '../../wrapper-components/AppHeader/AppHeader';
import { Sidebar } from '../../wrapper-components/Sidebar/Sidebar';

export interface AppPageLayoutProps {
  /**
   * Sidebar configuration for app-specific behavior (icons, URLs, handlers, etc.)
   */
  sidebarConfig: SidebarConfig;
  /**
   * Array of allowed apps/features to display in the sidebar
   */
  allowedApps?: Array<{
    appId: string;
    appName: string;
    features?: Array<{ featureId: string }>;
    additionalItems?: Array<{ featureId: string }>;
    sectionTitleId?: string;
  }>;
  /**
   * Function to get the section name for the current app.
   * This is app-specific and should be provided by the app.
   */
  getSectionName?: (appId?: string) => string;
  /**
   * Optional app ID for navigation and section name resolution.
   * If not provided, the first allowedApps appId will be used as fallback.
   */
  appId?: string;
  /**
   * The intl message ID for the platform name (e.g., "appHeader.platform.name", "appHeader.platform.name.internal")
   */
  platformNameMessageId: string;
  /**
   * When true, the sidebar section-title labels are omitted (nav items and
   * dividers are kept). Defaults to falsy.
   */
  // TODO: cleanup with cp_app_header_rename ff
  hideSectionTitles?: boolean;
}

export const AppPageLayout = ({
  sidebarConfig,
  allowedApps,
  getSectionName,
  appId,
  platformNameMessageId,
  hideSectionTitles,
}: AppPageLayoutProps) => {
  const theme = useTheme();
  // Using the navigation object for logo click
  const navigate = useNavigate();

  // Use provided appId or fallback to first allowedApps appId
  const effectiveAppId = appId ?? allowedApps?.[0]?.appId;
  // get sidebar items for the current user
  const sectionName = getSectionName ? getSectionName(effectiveAppId) : '';
  const { sidebarItems, isSidebarOpen } = useSidebar({
    config: sidebarConfig,
    allowedApps,
    hideSectionTitles,
  });

  const onLogoClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault();
      if (effectiveAppId) {
        navigate(`/${effectiveAppId}`);
      }
    },
    [effectiveAppId, navigate]
  );

  return (
    <Box
      data-testid="AppWrapperV2"
      sx={{
        width: '100%',
        height: '100%',
        minWidth: '1366px',
        minHight: '768px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <AppHeader
        appSectionName={sectionName}
        platformNameMessageId={platformNameMessageId}
        onLogoClick={onLogoClick}
      />

      <Box
        sx={{
          display: 'flex',
          height: 'calc(100% - 51px)',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Sidebar
          id="appSidebar"
          items={sidebarItems ?? []}
          selectedItem={''}
          isSidebarOpen={isSidebarOpen}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flexGrow: 1,
            overflowX: 'auto',
          }}
        >
          <Box
            data-testid="appContent"
            sx={{
              marginBottom: '80px',
              width: '100%',
            }}
          >
            <Outlet />
          </Box>

          <Footer />
        </Box>
      </Box>
    </Box>
  );
};
